import { useState, ChangeEvent, useEffect, useMemo } from "react";
import { Tooltip } from "react-tooltip";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "@headlessui/react";
import { CheckCircleIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import { handleEnterKey } from "../../app/keyboardUtils";
import { UserRole } from "../../app/userSlice";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { CustomSortIcon } from "../../components/DatatableHelpers";
import SimplePagination from "../../components/SimplePagination";
import TextWithTip from "../../components/TextWithTip";
import Select from "../../components/input/Select";
import SearchFilter from "../../components/input/SearchFilter";
import { useGetGradesQuery } from "../lookup/lookupApiSlice";
import {
	UserCourseViewModel,
	UserDistrictViewModel,
	UserSchoolViewModel,
} from "../users/usersApiSlice";
import {
	AvailableCoursesViewModel,
	CourseStatus,
	useAddUserToCoursesMutation,
	useLazyGetAvailableCoursesForUserQuery,
} from "./coursesApiSlice";

interface CoursesTableModel extends AvailableCoursesViewModel {
	isRowSelected: boolean;
}

interface Props {
	userId: number;
	userRole: UserRole;
	districtOptions: SimpleOption[];
	activeSchools: UserSchoolViewModel[];
	allSchoolCount: number;
	courses: UserCourseViewModel[];
	onClose: () => void;
	setErrorMessage: (message: string) => void;
}

export default function AddExistingCoursesToUserForm(props: Props) {
	const _displayLength = 5;

	const [availableCourses, setAvailableCourses] = useState<AvailableCoursesViewModel[]>([]);
	const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);

	const [page, setPage] = useState(0);
	const [isFiltered, setIsFiltered] = useState(false);
	const [sortField, setSortField] = useState<string>();
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);

	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);
	const [gradeOptions, setGradeOptions] = useState<SimpleOption[]>([]);

	const [districtId, setDistrictId] = useState<number>();
	const [schoolId, setSchoolId] = useState<number>();
	const [searchFilter, setSearchFilter] = useState("");
	const [gradeFilter, setGradeFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>(CourseStatus.Open);

	const [allCoursesLength, setAllCoursesLength] = useState(0);
	const [allChecked, setAllChecked] = useState(false);
	const [allPageChecked, setAllPageChecked] = useState(false);
	const [allPageCheckboxIds, setAllPageCheckboxIds] = useState<number[]>([]);

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const [callGetAvailableCourses, callGetAvailableCoursesResult] =
		useLazyGetAvailableCoursesForUserQuery();

	const [addUserToCourses] = useAddUserToCoursesMutation();

	useEffect(() => {
		const options: SimpleOption[] = [{ value: "", label: "All Grades" }];
		if (grades) {
			grades?.map((grade) => {
				options.push({ value: grade.gradeId.toString(), label: grade.name });
			});
		}
		setGradeFilter(options[0].value.toString());
		setGradeOptions(options);
	}, [grades]);

	useEffect(() => {
		if (props.districtOptions.length > 0) {
			setDistrictId(Number(props.districtOptions[0].value));
		}
	}, [props.districtOptions]);

	useEffect(() => {
		const options = props.activeSchools
			.filter((s) => s.districtID === districtId)
			.map((s) => ({ value: s.schoolID, label: s.name }) as SimpleOption);

		setSchoolOptions(options);

		if (options.length > 0) {
			setSchoolId(Number(options[0].value));
		}
	}, [districtId, props.activeSchools]);

	useEffect(() => {
		if (schoolId) {
			callGetAvailableCourses({ userId: props.userId, schoolId: schoolId }, true)
				.unwrap()
				.then((courses) => {
					setAvailableCourses(courses);
				})
				.catch((error: any) => {
					console.log(error);
				});
		}
	}, [schoolId]);

	useEffect(() => {
		setIsFiltered(!!searchFilter || statusFilter !== "All" || !!gradeFilter);
		setPage(0);
	}, [searchFilter, statusFilter, gradeFilter]);

	const filteredCourses = useMemo(() => {
		return (
			availableCourses.filter((course) => {
				if (searchFilter) {
					const searchLower = searchFilter.toLowerCase();
					const matchesSearch =
						course.name.toLowerCase().includes(searchLower) ||
						course.externalID?.toLowerCase().includes(searchLower);
					if (!matchesSearch) return false;
				}

				if (gradeFilter && course.gradeId.toString() !== gradeFilter) return false;

				if (statusFilter === CourseStatus.Open && course.active !== CourseStatus.Open)
					return false;
				if (
					statusFilter === CourseStatus.Inactive &&
					course.active !== CourseStatus.Inactive
				)
					return false;

				return true;
			}) ?? []
		);
	}, [availableCourses, searchFilter, gradeFilter, statusFilter]);

	const sortedCourses = useMemo(() => {
		if (!sortField || sortOrder === 0 || !sortOrder) {
			return filteredCourses;
		}

		const sorted = [...filteredCourses].sort((a, b) => {
			let aValue: any;
			let bValue: any;

			if (sortField === "gradeId") {
				aValue = a.gradeId;
				bValue = b.gradeId;

				if (aValue === bValue) return 0;
				return (aValue - bValue) * sortOrder;
			}

			aValue =
				(a[sortField as keyof AvailableCoursesViewModel] as string)?.toLowerCase() || null;
			bValue =
				(b[sortField as keyof AvailableCoursesViewModel] as string)?.toLowerCase() || null;

			// Push nulls to the end regardless of sort direction
			if (aValue === null && bValue === null) return 0;
			if (aValue === null) return 1;
			if (bValue === null) return -1;

			return (
				aValue.localeCompare(bValue, undefined, {
					numeric: true, // Allow numbers to be sorted in a numerical order
					sensitivity: "base",
				}) * sortOrder
			);
		});

		return sorted;
	}, [filteredCourses, sortField, sortOrder]);

	const onSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	const paginatedCourses = useMemo(() => {
		return sortedCourses.slice(page * _displayLength, (page + 1) * _displayLength);
	}, [sortedCourses, page]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const checkboxBodyTemplate = (row: CoursesTableModel) => {
		return (
			<input
				type="checkbox"
				value={row.id}
				checked={row.isRowSelected}
				onChange={handleCheckboxChange}
				className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
				aria-label={`Select course: ${row.name}`}
			/>
		);
	};

	const statusBodyTemplate = (row: CoursesTableModel) => {
		return (
			<span className="flex items-center gap-x-2">
				{row.active === CourseStatus.Open ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`status-${row.id}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`status-${row.id}-tooltip`}
					/>
				)}
				<Tooltip
					id={`status-${row.id}-tooltip`}
					place="top-start"
					content={`${row.active}`}
				/>
			</span>
		);
	};

	const nameBodyTemplate = (row: CoursesTableModel) => {
		return (
			<span>
				<TextWithTip uniqueName={`name-${row.id}`} text={row.name} />
			</span>
		);
	};

	const teachersBodyTemplate = (row: CoursesTableModel) => {
		return (
			<span className="whitespace-nowrap">
				<TextWithTip uniqueName={`teachers-${row.id}`} text={row.teachers.join(", ")} />
			</span>
		);
	};

	const gradeBodyTemplate = (row: CoursesTableModel) => {
		return <span>{grades?.find((g) => g.gradeId === row.gradeId)?.sortName}</span>;
	};

	function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setSelectedCourseIds((prev) => [...prev, Number(e.target.value)]);
		} else {
			setSelectedCourseIds((prev) => prev.filter((item) => item !== Number(e.target.value)));
		}
	}

	function handleSelectAll(isChecked: boolean) {
		if (isChecked) {
			setSelectedCourseIds((prev) => {
				const idsToAdd: number[] = [];
				allPageCheckboxIds.map((id) => {
					if (!prev.includes(id)) {
						idsToAdd.push(id);
					}
				});
				return [...prev, ...idsToAdd];
			});
		} else {
			setSelectedCourseIds((prev) =>
				prev.filter((item) => !allPageCheckboxIds.includes(item))
			);
		}
	}

	useEffect(() => {
		// the status of all courses being selected that were available
		if (selectedCourseIds.length === allCoursesLength && allCoursesLength != 0) {
			setAllChecked(true);
		} else {
			setAllChecked(false);
		}

		// the status of the entire page being selected
		if (
			allPageCheckboxIds.length > 0 &&
			allPageCheckboxIds.every((id) => selectedCourseIds.includes(id))
		) {
			setAllPageChecked(true);
		} else {
			setAllPageChecked(false);
		}
	}, [selectedCourseIds, allPageCheckboxIds, allCoursesLength]);

	useEffect(() => {
		const _temp: number[] = [];

		paginatedCourses.map((row) => {
			_temp.push(Number(row.id));
		});
		setAllPageCheckboxIds(_temp);
		setAllCoursesLength(availableCourses.length ?? 0);
	}, [paginatedCourses, availableCourses]);

	const canRegisterInCourses = useMemo(() => {
		return (
			props.userRole === UserRole.Proctor ||
			props.activeSchools.find((s) => s.schoolID == schoolId)?.seatsRemaining !== 0 ||
			props.courses.some((c) => c.schoolID == schoolId)
		);
	}, [schoolId, props.activeSchools, props.courses]);

	const coursesWithSelection: CoursesTableModel[] = useMemo(() => {
		return paginatedCourses.map((course) => ({
			...course,
			isRowSelected: selectedCourseIds.some((c) => c === course.id),
		}));
	}, [paginatedCourses, selectedCourseIds]);

	function handleSave() {
		if (selectedCourseIds.length > 0) {
			addUserToCourses({ userId: props.userId, courseIds: selectedCourseIds })
				.unwrap()
				.then((errors) => {
					let errorMessage = "";
					if (errors && errors.length > 0) {
						errorMessage = `The following courses could not be added for the user: `;
						errors.map((error) => {
							errorMessage += `\n${error}`;
						});
						scrollTo(0, 0);
					}

					props.setErrorMessage(errorMessage);
					props.onClose();
				})
				.catch((error) => {
					console.log(error.data);
				});
		}
	}

	function handleCancel() {
		props.onClose();
	}

	return (
		<div>
			<div className="mt-2 flex flex-row gap-2">
				{districtId !== undefined && props.districtOptions.length > 1 && (
					<Select
						value={districtId}
						setValue={(v) => setDistrictId(v as number)}
						label="District"
						simpleListBoxId="districtFilter"
						options={props.districtOptions}
						fieldClasses="w-1/2"
						simpleListBoxClasses="w-full"
					/>
				)}
				{schoolOptions.length > 0 && props.allSchoolCount > 1 && (
					<Select
						value={schoolId}
						setValue={(v) => setSchoolId(v as number)}
						label="School"
						simpleListBoxId="schoolFilter"
						options={schoolOptions}
						fieldClasses={props.districtOptions.length > 1 ? "w-1/2" : "w-full"}
						simpleListBoxClasses="w-full"
					/>
				)}
			</div>
			{canRegisterInCourses ? (
				availableCourses.length > 0 ? (
					<>
						<div className="mt-2 flex flex-row gap-2">
							<SearchFilter
								id="searchCourses"
								name="searchCourses"
								htmlFor="searchCourses"
								searchFilter={searchFilter}
								setSearchFilter={setSearchFilter}
								maxLength={125}
								label="Search"
								placeHolder="Search by name or external ID..."
								className="w-1/2"
								debounceDelay
							/>
							<Select
								value={gradeFilter}
								setValue={(v) => setGradeFilter(v as string)}
								label="Grade"
								simpleListBoxId="gradeFilter"
								options={gradeOptions}
								fieldClasses="w-1/4"
								simpleListBoxClasses="w-full"
							/>
							<Select
								value={statusFilter}
								setValue={(v) => setStatusFilter(v as string)}
								label="Status"
								simpleListBoxId="courseStatusFilter"
								options={[
									{
										value: "All",
										label: "All Courses",
									},
									{
										value: CourseStatus.Open,
										label: "Open Courses",
									},
									{
										value: CourseStatus.Inactive,
										label: "Inactive Courses",
									},
								]}
								fieldClasses="w-1/4"
							/>
						</div>
						<div className="mt-2">
							<div className="flex justify-between items-end">
								<p className="font-semibold text-sm p-1">
									{selectedCourseIds.length} course
									{selectedCourseIds.length == 1 ? "" : "s"} selected
								</p>
								<button
									className="text-xs p-1 hover:cursor-pointer focus:outline-hidden"
									onClick={() => setSelectedCourseIds([])}>
									[clear all]
								</button>
							</div>
							<DataTable
								value={coursesWithSelection}
								loading={callGetAvailableCoursesResult.isLoading}
								dataKey="id"
								emptyMessage="No courses found"
								stripedRows
								className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
								style={{ marginTop: "4px" }}
								sortField={sortField}
								sortOrder={sortOrder}
								onSort={onSort}
								sortIcon={CustomSortIcon}>
								<Column
									columnKey="course-checkbox"
									header={
										<input
											type="checkbox"
											checked={allChecked || allPageChecked}
											onChange={(e) => handleSelectAll(e.target.checked)}
											className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
											aria-label="Select all courses"
										/>
									}
									body={checkboxBodyTemplate}
									className="w-12"
									headerClassName="text-center"
									bodyClassName="text-center"
								/>
								<Column
									body={statusBodyTemplate}
									className="w-12"
									sortField="active"></Column>
								<Column
									header="Name"
									body={nameBodyTemplate}
									field="name"
									className="!max-w-52"
									sortable></Column>
								<Column header="External ID" field="externalID" sortable></Column>
								<Column
									header="Teacher(s)"
									body={teachersBodyTemplate}
									className="!max-w-52"></Column>
								<Column
									header="Grade"
									body={gradeBodyTemplate}
									sortField="gradeId"
									sortable></Column>
							</DataTable>
							<SimplePagination
								itemCount={sortedCourses?.length}
								page={page}
								pageSize={_displayLength}
								isFiltered={isFiltered}
								totalCount={availableCourses.length}
								onChange={(page) => handlePageChange(page)}
								className={"border-none -mt-2"}
							/>
						</div>
					</>
				) : (
					<div className="my-4 px-4 py-8 bg-gray-50 border border-gray-200 rounded-lg">
						<p className="text-gray-700 text-center">
							The user is already registered in all courses in the school.
						</p>
					</div>
				)
			) : (
				<div className="my-4 px-4 py-8 bg-gray-50 border border-gray-200 rounded-lg">
					<p className="text-gray-700 text-center">
						There are 0 seats remaining for the school. The student is not already
						registered in any courses in the school and cannot be added to a course.
						Please contact the MI Write team to request more seats.
					</p>
				</div>
			)}
			<div className="grid grid-cols-2 gap-3">
				<Button
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					id="confirm-button"
					disabled={selectedCourseIds.length === 0}
					onClick={handleSave}
					onKeyDown={(e) => handleEnterKey(e, handleSave)}
					className="btn btn-primary">
					Save
				</Button>
			</div>
		</div>
	);
}
